import React, { useEffect, useMemo, useState } from "react";
import { getI18n, useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Survey } from "survey-react-ui";
import { FunctionFactory, Model, settings } from "survey-core";
import {
  confirmActionAsyncFunc,
  CustomFunctionName,
  markdownHandler,
} from "../../../components/surveyjs/survey/survey.util";
import { initSurvey } from "../../../components/surveyjs/survey/init-survey";
import { cloneDeep, get } from "lodash";
import styles from "./edit-info.module.css";
import defaultAvatar from "../../../../../components/icon/assets/default-avatar.png";
import { formatRegionDisplay } from "../../../../../utils/common-utils";
import { useQuery } from "../../../../../utils/hooks/use-query";
import { TakeUIClickEventParameters } from "../../../../../common/ga/types/ga-general-type";
import { takeUIClickEvent } from "../../../../../common/ga/ga";
import { GlobalHelper } from "../../../../../utils/helpers/global-helper";
import { loginAsync, refreshTokenAsync } from "../../../../auth/redux/auth-slice";
import { commonSlice } from "../../../../../redux/common/common-slice";
import { getOid, sendOtpFunc } from "../../../../prudna/pages/personal-info-form/send-otp";
import { getFullRegionList, getRecruitPublicConfig } from "../../../../common/network/network";
import {
  editInfoByCreateAccount,
  fetchGlobalConfig,
  getInvitationStatus,
} from "../../../network/network";
import { BaseTemplate } from "./template";
import { ONBOARDING_PATH, ROOT_PATH } from "../../../../../routes/constants";
import { EditMode } from "../../candidate-data-form/utils";
import { backToApp, getI18nTranslation, getToken, parseJwt } from "../../../utils";
import { PruToast } from "../../../../../components/pru-toast";
import PHKLPageContainer from "../../../../../components/phkl-page-container/phkl-page-container";
import { OtpContextEnum, UserTypeEnum } from "../../../../prudna/network/enum";
import i18next from "i18next";
import { validateOtpFunc } from "../../otp-verification-screen/validate-otp";
import { updatePHKLCandidateProfile } from "../../../../auth/network/network";
import { setItem } from "../../../../../utils/local-storage";
import { RoleEnum } from "../../../types";
import { translate } from "../../../../../i18n/translation";
import { confirm } from "../../../../../components/modal/comfirm";

const { showLoadingAction, hideLoadingAction } = commonSlice.actions;
const elements = BaseTemplate.pages[0].elements;

export const EditInfoScreen = () => {
  initSurvey();
  // Create a wrapper function
  const wrappedSendOtpFunc = async (params: any[]) => {
    await sendOtpFunc(params);
  };
  // Replace the registered function since we need to add GA tracking in sendOtp
  FunctionFactory.Instance.unregister(CustomFunctionName.sendOtp);
  FunctionFactory.Instance.register(CustomFunctionName.sendOtp, wrappedSendOtpFunc);

  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const query: any = useQuery();
  const invitationId = location.state?.params?.invitationId;
  const params = location.state?.params;
  const lang = query.get("lang");
  const [formValues, setForm] = useState({} as any);
  const [otp, setOtp] = useState(false);
  const [pageInitiating, setPageInitiating] = useState(true);
  const [disabledBtn, setDisabledBtn] = useState(false);
  const [globalConfigs, setGlobalConfigs] = useState({} as any);
  const [recruitPublicConfigs, setRecruitPublicConfigs] = useState({} as any);
  const [oldPhone, setOldPhone] = useState("");
  const candidateProfile = location.state?.candidateProfile;
  const isNew = location.state?.isNew;

  const editMode: EditMode = useMemo(() => {
    if (invitationId) {
      return EditMode.NewWithInvitation;
    }
    if (isNew && candidateProfile) {
      return EditMode.NewWithLoginName;
    }
    if (candidateProfile) {
      return EditMode.EditBaseOnProfile;
    }
    return EditMode.NewWithInvitation;
  }, [invitationId, candidateProfile, isNew]);

  useEffect(() => {
    if (!invitationId && !candidateProfile) {
      PruToast({ message: "Wrong url, please check" });
      GlobalHelper.getGlobalDispatch()?.(hideLoadingAction());
      return;
    }

    const request = [getRecruitPublicConfig(), fetchGlobalConfig(), getFullRegionList()];
    if (editMode === EditMode.NewWithInvitation) {
      request.push(getInvitationStatus(invitationId));
    }

    Promise.all(request)
      .then((reses: any) => {
        const [recruitPublicConfigRes, globalConfigRes, regionConfigs, res] = reses;
        const regionPhoneField = elements.find((item: any) => item.name === "regionPhone");
        if (regionPhoneField?.templateElements?.[0] && regionConfigs) {
          const regionOpts = Object.values(regionConfigs).map((region: any) => ({
            text: formatRegionDisplay(region.code, region.displayName),
            key: region.code,
            value: region.code,
            reg: region.regex,
          }));
          // @ts-ignore
          regionPhoneField.templateElements[0].choices = regionOpts;
          setForm({
            regionCode: regionOpts?.[0].key ?? "+852",
          });
        } else {
          setForm({
            regionCode: "+852",
          });
        }

        setRecruitPublicConfigs(recruitPublicConfigRes.Recruitment);
        setGlobalConfigs(globalConfigRes.data?.content);
        GlobalHelper.setIdTypeConfigs(globalConfigRes.data?.content?.region?.idType);
        if (editMode === EditMode.NewWithInvitation) {
          const data = res?.data || res || {};
          if (!data.phone) {
            PruToast({
              message: t("Invalid Phone Number"),
            });
            setDisabledBtn(true);
            return;
          }

          const arrPhone = data.phone.split(" ");
          arrPhone.length = 2; // avoid out of range exception when using arrPhone[1]

          const nameValues = data.isNamesSet
            ? {
                firstname: data.firstname,
                lastname: data.lastname,
                localName: data.localName,
              }
            : { firstname: "", lastname: "", localName: "" };

          setForm({
            ...formValues,
            agentCode: data.agentCode,
            agentName: data.agentName,
            ...nameValues,
            noChineseName: !!data?.noChineseName,
            regionCode: arrPhone[0],
            phoneNumber: arrPhone[1],
          });
        } else if ([EditMode.NewWithLoginName, EditMode.EditBaseOnProfile].includes(editMode)) {
          const arrPhone = candidateProfile?.phone?.split(" ") || [];

          const nameValues = candidateProfile?.isNamesSet
            ? {
                firstname: candidateProfile?.firstname,
                lastname: candidateProfile?.lastname,
                localName: candidateProfile?.localName,
              }
            : { firstname: "", lastname: "", localName: "" };

          setForm({
            ...formValues,
            agentCode: candidateProfile?.agentid,
            agentName: candidateProfile?.agentName,
            ...nameValues,
            noChineseName: !!candidateProfile?.noChineseName,
            regionCode: arrPhone[0],
            phoneNumber: arrPhone[1],
          });
          setOldPhone(candidateProfile?.phone);
        }
      })
      .finally(() => {
        setPageInitiating(false);
      });
  }, []);

  useEffect(() => {
    if (lang) {
      setItem("i18nextLng", lang.toLocaleLowerCase());
      i18n.changeLanguage(lang.toLocaleLowerCase());
    }
  }, [lang]);

  const template = useMemo(() => {
    const _template = cloneDeep(BaseTemplate);
    convertTemplate(_template);
    return _template;
  }, [i18next.language, pageInitiating]);

  const survey = useMemo(() => {
    if (pageInitiating) return null;
    const elements = template.pages[0].elements;
    if ([EditMode.NewWithInvitation, EditMode.NewWithLoginName].includes(editMode)) {
      //disable regionPhone
      const regionPhoneField = elements.find((element) => element.name === "regionPhone");
      if (regionPhoneField) {
        regionPhoneField.readOnly = true;
        regionPhoneField.templateElements?.map((te: any) => {
          te.readOnly = true;
        });
      }
    } else if (editMode === EditMode.EditBaseOnProfile) {
      //enable regionPhone
      const regionPhoneField = elements.find((element) => element.name === "regionPhone");
      if (regionPhoneField) {
        regionPhoneField.readOnly = false;
        regionPhoneField.templateElements?.map((te: any) => {
          te.readOnly = false;
        });
      }
      const otpField: any = elements.find((item: any) => item.name === "otp");
      const otpConfig = {
        ...otpField.otpConfig,
        actionParams: {
          context: OtpContextEnum.CW_EDIT_MOBILE,
          username: GlobalHelper.getUserName(),
          userType: "CANDIDATE",
          deliveryMethod: "SMS",
        },
      };
      otpField.otpConfig = otpConfig;
    }
    const survey = new Model(cloneDeep(template));
    survey.showPageTitles = false;
    survey.showNavigationButtons = "none";
    survey.questionErrorLocation = "bottom";
    survey.showCompletedPage = false;

    const agentName = formValues?.agentName?.[getI18n().language === "zhhk" ? "zhHk" : "enUs"];
    const defaultValue: any = {
      isNew: isNew || !!invitationId,
      firstname: formValues.firstname ?? "",
      lastname: formValues.lastname ?? "",
      localName: formValues.localName ?? "",
      noChineseName: formValues.noChineseName,
      yourRecruitingAgent: `${formValues?.agentCode}, ${agentName || ""}`,
      languagePreference: formValues.languagePreference,
      regionPhone: [
        {
          regionCode: formValues.regionCode ?? "",
          phoneNumber: formValues.phoneNumber,
        },
      ],
    };
    survey.mergeData(defaultValue);
    survey.onComplete.add((sender, options) => {});

    const localName = survey.getQuestionByName("localName");
    if (formValues.noChineseName) {
      localName.isRequired = false;
      localName.value = "";
      localName.readOnly = true;
    } else {
      localName.isRequired = true;
      localName.readOnly = false;
    }

    survey.onValueChanged.add(async (_sender: any, options: any) => {
      if (options.name === "noChineseName") {
        const localName = survey.getQuestionByName("localName");
        if (!options.value) {
          localName.isRequired = true;
          localName.readOnly = false;
        } else {
          localName.isRequired = false;
          localName.value = "";
          localName.readOnly = true;
        }
      }
    });
    survey.onTextMarkdown.add(markdownHandler);
    survey.onGetQuestionTitleActions.add((_, options) => {
      if (options.question.tooltips) {
        options.titleActions = [
          {
            id: "show-popover",
            component: "popover",
            tooltip: options.question.tooltips,
          },
        ];
      }
    });

    settings.confirmActionAsync = confirmActionAsyncFunc;
    return survey;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageInitiating, template]);

  const submitCanidateData = (candidateData: any) => {
    GlobalHelper.getGlobalDispatch()?.(showLoadingAction());

    if (editMode === EditMode.NewWithInvitation) {
      editInfoByCreateAccount({
        ...candidateData,
        localName: candidateData.noChineseName ? "" : candidateData.localName,
      })
        .then(async (res: any) => {
          confirm({
            message: t("account_creation_completion_reminder"),
            confirmButtonText: t("global.text.confirm"),
            hideCancelButton: true,
            messageStyle: { textAlign: "left" },
          }).then(async (result) => {
            if (result.ok) {
              navigate(`${ROOT_PATH}/login`);
            }
          });
        })
        .catch((err) => {
          PruToast({ message: getI18n().t("Common.server_error") });
        })
        .finally(() => {
          GlobalHelper.getGlobalDispatch()?.(hideLoadingAction());
        });
    } else if ([EditMode.NewWithLoginName, EditMode.EditBaseOnProfile].includes(editMode)) {
      updatePHKLCandidateProfile({
        candidateData: {
          ...candidateData,
          localName: candidateData.noChineseName ? "" : candidateData.localName,
        },
      })
        .then(async (res: any) => {
          PruToast({ message: getI18n().t("Common.updated_success") });
          if (editMode === EditMode.EditBaseOnProfile) {
            navigate(-1);
          } else {
            await dispatch<any>(refreshTokenAsync());
            // navigate(`${ROOT_PATH}/${ONBOARDING_PATH}/candidate-home`);
            window.location.href = `${ROOT_PATH}/${ONBOARDING_PATH}/candidate-home`;
            backToApp();
          }
        })
        .catch((err) => {
          const errorKey = get(err, "message");
          const message = getI18nTranslation(errorKey) ?? errorKey;
          PruToast({ message: message || getI18n().t("Common.server_error") });
        })
        .finally(() => {
          GlobalHelper.getGlobalDispatch()?.(hideLoadingAction());
        });
    }
    const gaData: TakeUIClickEventParameters = {
      module: "Login",
      feature: "CandidateLogin",
      journey: "create_account",
      stage: "submit_candidate_data_form",
      screen_id: "SCR_LGN_CNDDT_DT_FRM",
      screen_name: "Login-Candidate-Data-Form-Screen",
      object_name: "Confirm",
      object_type: "BUTTON",
    };
    takeUIClickEvent(gaData);
  };

  const handleOnConfirm = async () => {
    if (!survey) return null;
    if (!survey.validateCurrentPage()) {
      return;
    }

    if (editMode === EditMode.EditBaseOnProfile) {
      const phone = `${survey.data.regionPhone[0].regionCode} ${survey.data.regionPhone[0].phoneNumber}`;
      if (oldPhone !== phone) {
        if (!survey.data.otp) {
          const otpQuestion = survey.getQuestionByName("otp");
          otpQuestion.addError(t("Common.this_field_is_mandatory"));
          return;
        }

        const oid = getOid();
        if (!oid) {
          const otpQuestion = survey.getQuestionByName("otp");
          otpQuestion.addError(t("Common.incorrect_verification_code"));
          return;
        } else {
          const surveyData = survey.data;
          const res = await validateOtpFunc({
            context: OtpContextEnum.CW_EDIT_MOBILE,
            otp: `${getOid()}:${surveyData.otp}`,
            regionCode: surveyData.regionPhone[0].regionCode,
            phone: surveyData.regionPhone[0].phoneNumber,
            userType: UserTypeEnum.CANDIDATE,
          });
          if (res !== true) {
            if (typeof res === "string") {
              const otpQuestion = survey.getQuestionByName("otp");
              otpQuestion.addError(res);
            }
            return;
          }
        }
      }
    }

    const surveyData = survey.data;
    const candidateData: any = {};
    if (editMode === EditMode.NewWithInvitation) {
      candidateData.inviteId = invitationId;
      candidateData.firstname = surveyData.firstname;
      candidateData.lastname = surveyData.lastname;
      candidateData.localName = surveyData.localName;
      candidateData.noChineseName = surveyData.noChineseName;
      candidateData.regionCode = survey.data.regionPhone[0].regionCode;
      candidateData.phoneNumber = survey.data.regionPhone[0].phoneNumber;
      candidateData.isAgreedPolicy = surveyData.privacyPolicyAgreement;
      candidateData.isUpdate = false;
      candidateData.agentCode = formValues.agentCode;
      candidateData.agentName = formValues.agentName;
    } else {
      candidateData.firstname = surveyData.firstname;
      candidateData.lastname = surveyData.lastname;
      candidateData.localName = surveyData.localName;
      candidateData.noChineseName = surveyData.noChineseName;
      candidateData.countryCode = survey.data.regionPhone[0].regionCode;
      candidateData.phoneNumber = survey.data.regionPhone[0].phoneNumber;
      candidateData.reviewedAndAgreeToPICS = surveyData.privacyPolicyAgreement;
    }

    submitCanidateData(candidateData);
  };

  const handleValueChanged = (sender: any, options: any) => {
    if (options.name === "otp") {
      setOtp(options.value);
    }
    if (options.name === "regionPhone") {
      setForm({
        regionCode: options.value[0].regionCode,
        phoneNumber: options.value[0].phoneNumber,
      });
    }
  };

  return (
    <PHKLPageContainer
      title={t("Recruitment.edit_profile")}
      showBackBtn={editMode === EditMode.EditBaseOnProfile}
    >
      {!pageInitiating && (
        <div className="flex flex-1 flex-col max-w-md px-6">
          <div className="flex flex-col w-full pb-4">
            <div className="bg-white w-full rounded-tr-3xl h-10"></div>

            {/* profile Image */}
            {/* <div className="mb-4">
              <div className={styles.profileHeader}>
                <div className={styles.profilePictureContainer}>
                  <label htmlFor="fileInput">
                    <img src={defaultAvatar} alt="" className={styles.profileImage} />
                  </label>
                </div>
              </div>
            </div> */}

            <Survey
              currentPageNo={0}
              className="survey-common"
              id="create-account"
              model={survey}
              onValueChanged={handleValueChanged}
            />
          </div>
          <div className=" w-full bg-white pb-7 flex items-center justify-between gap-4">
            <button
              className="bg-red-500 flex-1 text-white font-semibold py-3 rounded-lg shadow-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-opacity-75 disabled:bg-[#CCCCCC] disabled:shadow-none"
              disabled={disabledBtn}
              onClick={handleOnConfirm}
            >
              {t("global.text.confirm")}
            </button>
          </div>
        </div>
      )}
    </PHKLPageContainer>
  );
};

const convertTemplate = (template: any) => {
  // don't mutate the original template
  // const template: any = cloneDeep(BaseTemplate);

  // Function to translate a given field if it exists
  const translateField = (obj: any, field: string) => {
    if (obj?.[field]) {
      obj[field] = getI18n().t(obj[field]);
    }
  };

  // Loop through each element in the first page
  template.pages[0].elements = template.pages[0].elements.map((q: any) => {
    // Translate title, labelTrue, labelFalse, requiredErrorText fields
    translateField(q, "title");
    translateField(q, "placeholder");
    translateField(q, "labelTrue");
    translateField(q, "labelFalse");
    translateField(q, "requiredErrorText");
    translateField(q, "tooltips");
    translateField(q?.modalConfig, "modalTitle");

    if (q?.type === "html") {
      const htmlString = q.html;
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = htmlString;
      const paragraphs = tempDiv.getElementsByTagName("p");
      Array.from(paragraphs).forEach((p) => {
        const content = translate((p.textContent as string).trim());
        p.textContent = content;
      });
      const newHtmlString = tempDiv.innerHTML;
      q.html = newHtmlString;
    }

    // translate choices if they exist
    if (q.choices && Array.isArray(q.choices)) {
      q.choices.forEach((choice: any) => {
        translateField(choice, "text");
      });
    }

    // Check if the element has validators and translate their text fields
    if (q.validators && Array.isArray(q.validators)) {
      q.validators.forEach((validator: any) => {
        translateField(validator, "text");
      });
    }

    // If the element is a paneldynamic type, we need to handle nested templateElements
    if (q.type === "paneldynamic" && q.templateElements && Array.isArray(q.templateElements)) {
      q.templateElements = q.templateElements.map((te: any) => {
        translateField(te, "title");
        translateField(te, "labelTrue");
        translateField(te, "labelFalse");
        translateField(te, "requiredErrorText");
        translateField(te, "placeholder");
        translateField(te?.modalConfig, "modalTitle");

        if (te.validators && Array.isArray(te.validators)) {
          te.validators.forEach((validator: any) => {
            translateField(validator, "text");
          });
        }

        return te;
      });
    }

    return q;
  });
  return template;
};
